import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Fade,
  Tooltip,
  Avatar,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  Pets as VetAIIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Lightbulb as LightbulbIcon
} from '@mui/icons-material';
import { openaiAssistantService } from '../services/openaiAssistantService';

const AsistenteIA = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Estados principales
  const [mensaje, setMensaje] = useState('');
  const [historialChat, setHistorialChat] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [sugerencias, setSugerencias] = useState([]);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(true);
  
  // Referencia para scroll automático
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);

  // Cargar sugerencias iniciales
  useEffect(() => {
    if (open) {
      cargarSugerenciasIniciales();
      // Focus en el input cuando se abre
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  }, [open]);

  // Scroll automático cuando hay nuevos mensajes
  useEffect(() => {
    if (chatContainerRef.current && historialChat.length > 0) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [historialChat]);

  const cargarSugerenciasIniciales = async () => {
    try {
      const sugerenciasObtenidas = await openaiAssistantService.obtenerSugerencias();
      setSugerencias(sugerenciasObtenidas);
    } catch (error) {
      console.error('Error cargando sugerencias:', error);
    }
  };

  const enviarMensaje = async () => {
    if (!mensaje.trim() || cargando) return;

    const mensajeUsuario = mensaje.trim();
    setMensaje('');
    setCargando(true);
    setMostrarSugerencias(false);

    // Agregar mensaje del usuario al historial
    const nuevoHistorial = [
      ...historialChat,
      {
        role: 'user',
        content: mensajeUsuario,
        timestamp: new Date()
      }
    ];
    setHistorialChat(nuevoHistorial);

    try {
      // Preparar historial para OpenAI (solo role y content)
      const historialParaAPI = historialChat.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Obtener respuesta del asistente
      const resultado = await openaiAssistantService.chat(mensajeUsuario, historialParaAPI);

      // Agregar respuesta del asistente
      setHistorialChat(prev => [
        ...prev,
        {
          role: 'assistant',
          content: resultado.respuesta,
          timestamp: new Date(),
          tokensUsados: resultado.tokensUsados
        }
      ]);

    } catch (error) {
      console.error('Error enviando mensaje:', error);
      setHistorialChat(prev => [
        ...prev,
        {
          role: 'assistant',
          content: '❌ Lo siento, hubo un error procesando tu mensaje. Por favor intenta de nuevo.',
          timestamp: new Date(),
          error: true
        }
      ]);
    } finally {
      setCargando(false);
    }
  };

  const manejarEnter = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviarMensaje();
    }
  };

  const usarSugerencia = (sugerencia) => {
    setMensaje(sugerencia);
    setMostrarSugerencias(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const limpiarChat = () => {
    setHistorialChat([]);
    setMostrarSugerencias(true);
    cargarSugerenciasIniciales();
  };

  const cerrarYLimpiar = () => {
    onClose();
    // Limpiar después de un pequeño delay para evitar flicker
    setTimeout(() => {
      setHistorialChat([]);
      setMensaje('');
      setMostrarSugerencias(true);
    }, 300);
  };

  // Sugerencias rápidas predefinidas
  const sugerenciasRapidas = [
    "¿Cuántos pacientes hay en total?",
    "Muéstrame las estadísticas",
    "¿Hay pacientes críticos?",
    "¿Cuántos pacientes están activos?",
    "¿Qué productos tenemos en inventario?",
    "¿Hay productos con stock bajo?",
    "¿Cuándo vence la amoxicilina?",
    "¿Cuánto cuesta la amoxicilina?",
    "¿Cuál es el precio de compra del ankofen?",
    "¿Qué proveedor tiene las vitaminas?",
    "¿En qué categoría está el desparasitante?",
    "¿Cuál es el margen de ganancia de las vacunas?",
    "Muéstrame información completa de un producto"
  ];

  return (
    <Dialog
      open={open}
      onClose={cerrarYLimpiar}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? '100vh' : '80vh',
          maxHeight: isMobile ? '100vh' : '80vh',
          borderRadius: isMobile ? 0 : 2
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#c46494',
        color: 'white',
        py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VetAIIcon sx={{ fontSize: '1.5rem' }} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            Asistente IA - Belkysveterinaria
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Limpiar conversación">
            <IconButton
              onClick={limpiarChat}
              sx={{ color: 'white' }}
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cerrar">
            <IconButton
              onClick={cerrarYLimpiar}
              sx={{ color: 'white' }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Área de chat */}
        <Box
          ref={chatContainerRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          {/* Mensaje de bienvenida */}
          {historialChat.length === 0 && (
            <Fade in timeout={500}>
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  backgroundColor: '#f8f9fa',
                  border: '2px solid #fcdcf4',
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ backgroundColor: '#c46494', mr: 2 }}>
                    <VetAIIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ color: '#602442', fontWeight: 'bold' }}>
                    ¡Hola! Soy tu asistente de Belkysveterinaria
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ color: '#602442', mb: 2 }}>
                  Puedo ayudarte con información completa sobre:
                  <br />• <strong>Pacientes:</strong> Historia clínica, propietarios, tratamientos
                  <br />• <strong>Inventario:</strong> Stock, precios de compra/venta, vencimientos
                  <br />• <strong>Productos:</strong> Proveedores, categorías, presentaciones, márgenes
                  <br />• <strong>Estadísticas:</strong> Reportes completos de la clínica
                </Typography>
                
                {/* Sugerencias automáticas */}
                {sugerencias.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#a55e88', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LightbulbIcon sx={{ fontSize: '1rem' }} />
                      Información importante:
                    </Typography>
                    {sugerencias.map((sugerencia, index) => (
                      <Typography key={index} variant="body2" sx={{ color: '#602442', mb: 0.5 }}>
                        {sugerencia}
                      </Typography>
                    ))}
                  </Box>
                )}

                {/* Sugerencias rápidas */}
                {mostrarSugerencias && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#a55e88', mb: 1 }}>
                      Preguntas frecuentes:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {sugerenciasRapidas.map((sugerencia, index) => (
                        <Chip
                          key={index}
                          label={sugerencia}
                          onClick={() => usarSugerencia(sugerencia)}
                          variant="outlined"
                          sx={{
                            borderColor: '#c46494',
                            color: '#602442',
                            fontSize: '0.8rem',
                            '&:hover': {
                              backgroundColor: '#fcdcf4',
                              borderColor: '#a55e88'
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Fade>
          )}

          {/* Mensajes del chat */}
          {historialChat.map((msg, index) => (
            <Fade key={index} in timeout={300}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 1
                }}
              >
                <Box
                  sx={{
                    maxWidth: '70%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    flexDirection: msg.role === 'user' ? 'row-reverse' : 'row'
                  }}
                >
                  <Avatar
                    sx={{
                      backgroundColor: msg.role === 'user' ? '#602442' : '#c46494',
                      width: 32,
                      height: 32
                    }}
                  >
                    {msg.role === 'user' ? <PersonIcon /> : <VetAIIcon />}
                  </Avatar>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      backgroundColor: msg.role === 'user' ? '#602442' : '#f8f9fa',
                      color: msg.role === 'user' ? 'white' : '#602442',
                      borderRadius: 2,
                      border: msg.error ? '1px solid #f44336' : 'none'
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        lineHeight: 1.6
                      }}
                    >
                      {msg.content}
                    </Typography>
                    {msg.tokensUsados && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 1,
                          opacity: 0.7,
                          fontSize: '0.7rem'
                        }}
                      >
                        Tokens: {msg.tokensUsados}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              </Box>
            </Fade>
          ))}

          {/* Indicador de carga */}
          {cargando && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Avatar sx={{ backgroundColor: '#c46494', width: 32, height: 32 }}>
                  <VetAIIcon />
                </Avatar>
                <Paper
                  elevation={2}
                  sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: '#c46494' }} />
                    <Typography variant="body2" sx={{ color: '#602442' }}>
                      Procesando tu consulta...
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Box>
          )}
        </Box>

        <Divider />

        {/* Área de input */}
        <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              ref={inputRef}
              fullWidth
              multiline
              maxRows={3}
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              onKeyDown={manejarEnter}
              placeholder="Escribe tu pregunta aquí... (Ej: ¿Cómo está Max? o Muéstrame las estadísticas)"
              disabled={cargando}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#c46494',
                  },
                  '&:hover fieldset': {
                    borderColor: '#a55e88',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#602442',
                  },
                },
              }}
            />
            <Button
              onClick={enviarMensaje}
              disabled={!mensaje.trim() || cargando}
              variant="contained"
              sx={{
                backgroundColor: '#c46494',
                '&:hover': { backgroundColor: '#a55e88' },
                minWidth: 'auto',
                px: 2,
                py: 1.5
              }}
            >
              {cargando ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : (
                <SendIcon />
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AsistenteIA;